import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal, Form, Button, Input, Select, DatePicker,
    InputNumber, Switch, Divider, Typography, Space
} from 'antd';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import styles from './CustomReportConfigModal.module.css';

const { Text } = Typography;
const { Option } = Select;

const CustomReportConfigModal = ({
                                     visible,
                                     reportInfo,
                                     onClose,
                                     onRunReport
                                 }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Reset form when modal becomes visible or report changes
    useEffect(() => {
        if (visible && reportInfo) {
            form.resetFields();

            // Set default values if any
            const defaultValues = {};
            Object.entries(reportInfo.parameters || {}).forEach(([key, param]) => {
                if (param.default !== undefined) {
                    // Handle date types
                    if (param.type === 'date' || param.type === 'datetime') {
                        defaultValues[key] = param.default ? dayjs(param.default) : null;
                    } else {
                        defaultValues[key] = param.default;
                    }
                }
            });

            if (Object.keys(defaultValues).length > 0) {
                form.setFieldsValue(defaultValues);
            }
        }
    }, [visible, reportInfo, form]);

    // Handle form submission
    const handleSubmit = useCallback(async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            // Process values for specific types
            const processedValues = {};
            Object.entries(values).forEach(([key, value]) => {
                const paramDef = reportInfo.parameters[key];

                // Format dates according to parameter type
                if (paramDef && value) {
                    if (paramDef.type === 'date') {
                        processedValues[key] = dayjs(value).format('YYYY-MM-DD');
                    } else if (paramDef.type === 'datetime') {
                        processedValues[key] = dayjs(value).format('YYYY-MM-DDTHH:mm:ss');
                    } else {
                        processedValues[key] = value;
                    }
                } else {
                    processedValues[key] = value;
                }
            });

            onRunReport(reportInfo.name, processedValues);
            onClose();
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setLoading(false);
        }
    }, [form, reportInfo, onRunReport, onClose]);

    // Render form items based on parameter definitions
    const renderFormItems = () => {
        if (!reportInfo || !reportInfo.parameters) return null;

        return Object.entries(reportInfo.parameters).map(([key, param]) => {
            const label = param.label || key;
            const required = param.required || false;

            // Render different form items based on parameter type
            switch (param.type) {
                case 'string':
                    return (
                        <Form.Item
                            key={key}
                            name={key}
                            label={label}
                            required={required}
                            tooltip={param.description ? { title: param.description } : null}
                        >
                            <Input placeholder={param.placeholder || `Enter ${label}`} />
                        </Form.Item>
                    );

                case 'number':
                case 'integer':
                    return (
                        <Form.Item
                            key={key}
                            name={key}
                            label={label}
                            required={required}
                            tooltip={param.description ? { title: param.description } : null}
                        >
                            <InputNumber
                                className={styles.inputNumber}
                                placeholder={param.placeholder || `Enter ${label}`}
                                min={param.min}
                                max={param.max}
                                step={param.type === 'integer' ? 1 : undefined}
                            />
                        </Form.Item>
                    );

                case 'boolean':
                    return (
                        <Form.Item
                            key={key}
                            name={key}
                            label={label}
                            valuePropName="checked"
                            required={required}
                            tooltip={param.description ? { title: param.description } : null}
                        >
                            <Switch />
                        </Form.Item>
                    );

                case 'date':
                    return (
                        <Form.Item
                            key={key}
                            name={key}
                            label={label}
                            required={required}
                            tooltip={param.description ? { title: param.description } : null}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    );

                case 'datetime':
                    return (
                        <Form.Item
                            key={key}
                            name={key}
                            label={label}
                            required={required}
                            tooltip={param.description ? { title: param.description } : null}
                        >
                            <DatePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                    );

                case 'select':
                    return (
                        <Form.Item
                            key={key}
                            name={key}
                            label={label}
                            required={required}
                            tooltip={param.description ? { title: param.description } : null}
                        >
                            <Select
                                placeholder={param.placeholder || `Select ${label}`}
                                allowClear={!required}
                                mode={param.multiple ? 'multiple' : undefined}
                            >
                                {(param.options || []).map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label || option.value}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    );

                default:
                    return (
                        <Form.Item
                            key={key}
                            name={key}
                            label={label}
                            required={required}
                            tooltip={param.description ? { title: param.description } : null}
                        >
                            <Input placeholder={param.placeholder || `Enter ${label}`} />
                        </Form.Item>
                    );
            }
        });
    };

    return (
        <Modal
            title={`Configure Report: ${reportInfo?.title || 'Custom Report'}`}
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleSubmit}
                    loading={loading}
                >
                    Run Report
                </Button>
            ]}
            destroyOnClose
            width={520}
        >
            {reportInfo?.description && (
                <div className={styles.description}>
                    <Text type="secondary">{reportInfo.description}</Text>
                </div>
            )}

            <Form
                form={form}
                layout="vertical"
                name="reportConfigForm"
                className={styles.formContainer}
            >
                {Object.keys(reportInfo?.parameters || {}).length > 0 ? (
                    renderFormItems()
                ) : (
                    <div className={styles.noParamsContainer}>
                        <InfoCircleOutlined className={styles.infoIcon} />
                        <Text type="secondary">This report doesn't require any parameters</Text>
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default React.memo(CustomReportConfigModal);
